import React, { Suspense, lazy, useState, useEffect, type ReactNode, type ReactElement } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AlertTriangle } from "lucide-react";
import LoginPage from "./pages/LoginPage";
import { supabase } from "./lib/supabase";
import { initializeStore } from "./lib/store";
import { TooltipProvider } from "./components/ui/tooltip";
import { TasksProvider } from "./contexts/TasksContext";

// Code-splitting : seules LoginPage et le shell sont dans le bundle initial.
// Sans ça, les 18 pages (+ recharts, framer-motion, SDKs IA) étaient
// téléchargées et parsées juste pour afficher le formulaire de connexion.
const Layout = lazy(() => import("./components/Layout"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const ProfitPage = lazy(() => import("./pages/ProfitPage"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const ConfigPage = lazy(() => import("./pages/ConfigPage"));
const BannersPage = lazy(() => import("./pages/BannersPage"));
const AIDashboardPage = lazy(() => import("./pages/AIDashboardPage"));
const ProductRequestsPage = lazy(() => import("./pages/ProductRequestsPage"));
const CandidaturesPage = lazy(() => import("./pages/CandidaturesPage"));
const VendorsPage = lazy(() => import("./pages/VendorsPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const PayoutsPage = lazy(() => import("./pages/PayoutsPage"));

const queryClient = new QueryClient();

// ─── Error Boundary ─────────────────────────────────────────────────────────

interface ErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class AppErrorBoundary extends React.Component<{ children: ReactElement }, ErrorBoundaryState> {
  constructor(props: { children: ReactElement }) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(err: unknown): ErrorBoundaryState {
    const msg = err instanceof Error ? err.message : "Une erreur inattendue est survenue.";
    return { hasError: true, message: msg };
  }

  componentDidCatch(err: unknown, info: React.ErrorInfo) {
    // Log removed for production
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0B0B0E] flex flex-col items-center justify-center gap-4 p-8">
          <div className="w-12 h-12 rounded-full bg-rose-500/20 flex items-center justify-center">
            <span className="text-rose-400 text-2xl font-bold">!</span>
          </div>
          <div className="text-center max-w-sm">
            <h2 className="text-lg font-bold text-white mb-2">Une erreur est survenue</h2>
            <p className="text-sm text-[#A1A1AA]">{this.state.message}</p>
          </div>
          <button
            className="px-6 py-2.5 rounded-xl bg-brand-accent text-white font-bold text-sm hover:opacity-90 transition-opacity shadow-lg shadow-brand-accent/20"
            onClick={() => window.location.reload()}
          >
            Recharger la page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// ─── Protected Route ────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-[#0B0B0E] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-brand-accent flex items-center justify-center shadow-lg shadow-brand-accent/20">
          <svg className="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
        <p className="text-[#71717A] text-sm font-medium">Chargement...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children?: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [initError, setInitError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (cancelled) return;
      setInitError(false);
      try {
        // Session d'abord (lecture locale, pas de réseau) : un visiteur non
        // connecté est redirigé vers /login sans attendre le moindre fetch.
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;

        if (!data.session) {
          setAuthenticated(false);
          setLoading(false);
          return;
        }

        const userId = data.session.user.id;
        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .in("role", ["super_admin", "admin", "administrator"])
          .limit(1);

        if (cancelled) return;
        if (roleError) throw roleError;

        const isAdmin = Boolean(roleData && roleData.length > 0);
        setAuthenticated(isAdmin);
        if (isAdmin) {
          // loadAllFromSQL charge déjà vendors/candidatures/boutiques/payouts :
          // inutile d'enchaîner refreshMarketplaceFromSQL ensuite (doublon).
          await initializeStore();
          if (cancelled) return;
        }
        setLoading(false);
      } catch (err) {
        if (cancelled) return;
        // Une exception ici (réseau, timeout, etc.) ne doit pas déconnecter
        // l'utilisateur : on vérifie si une session valide existe encore.
        try {
          const { data } = await supabase.auth.getSession();
          if (cancelled) return;
          if (data.session) {
            setInitError(true);
          } else {
            setAuthenticated(false);
          }
        } catch {
          if (!cancelled) setInitError(true);
        }
        setLoading(false);
      }
    };

    void init();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      if (!cancelled) void init();
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [retryKey]);

  const handleRetry = () => {
    setInitError(false);
    setLoading(true);
    setRetryKey((k) => k + 1);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (initError) {
    return (
      <div className="min-h-screen bg-[#0B0B0E] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
          </div>
          <div className="text-center">
            <h2 className="text-base font-bold text-white">Impossible de charger le panneau</h2>
            <p className="text-[#71717A] text-sm font-medium mt-1">Vérifiez votre connexion puis réessayez.</p>
          </div>
          <button
            onClick={handleRetry}
            className="px-6 py-2.5 rounded-xl bg-brand-accent text-white font-bold text-sm hover:opacity-90 transition-opacity shadow-lg shadow-brand-accent/20"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/login" replace />;
  }

  return children ?? <Outlet />;
}

// ─── App ────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <TasksProvider>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                element={
                  <ProtectedRoute>
                    <AppErrorBoundary>
                      <Suspense fallback={<LoadingScreen />}>
                        <Layout>
                          <Outlet />
                        </Layout>
                      </Suspense>
                    </AppErrorBoundary>
                  </ProtectedRoute>
                }
              >
                <Route path="/" element={<Dashboard />} />
                <Route path="/orders" element={<OrdersPage />} />
                <Route path="/catalogue" element={<CatalogPage />} />
                <Route path="/profit" element={<ProfitPage />} />
                <Route path="/tasks" element={<TasksPage />} />
                <Route path="/config" element={<ConfigPage />} />
                <Route path="/banners" element={<BannersPage />} />
                <Route path="/ai-agent" element={<AIDashboardPage />} />
                <Route path="/requests" element={<ProductRequestsPage />} />
                <Route path="/candidatures" element={<CandidaturesPage />} />
                <Route path="/vendors" element={<VendorsPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route path="/payouts" element={<PayoutsPage />} />
              </Route>
            </Routes>
          </TasksProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}
